/**
 * @file LoginPage.jsx
 * Pagina di accesso con autenticazione SSO Microsoft (OAuth2 Azure AD).
 * Layout a due pannelli: branding blu a sinistra, form login bianco a destra.
 * Su mobile il pannello sinistro è nascosto.
 */

import { useApp } from '../hooks/useApp';
import { msalInstance, msalReady, loginRequest } from '../lib/msalConfig';

/**
 * Pagina di login.
 * Avvia il flusso OAuth2 Azure via MSAL (client-side), che ottiene un Azure ID token
 * poi scambiato con il BE per un JWT applicativo (cookie httpOnly).
 * Mostrata solo quando azureEnabled=true e l'utente non è autenticato.
 */
export default function LoginPage() {
  const { azureEnabled } = useApp();

  const handleLogin = async () => {
    await msalReady;
    msalInstance.loginRedirect(loginRequest);
  };

  // Questa pagina non dovrebbe mai essere mostrata quando Azure è disabilitato:
  // AuthGate imposta hasAccess=true in quel caso e non renderizza LoginPage.
  // Rimane come fallback di sicurezza.
  if (!azureEnabled) return null;

  return (
    <div className="min-h-screen w-full flex">

      {/* Pannello sinistro — branding blu */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-14 bg-blue-600 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-blue-500/40" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blue-700/40 -translate-x-1/3 translate-y-1/3" />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-5 h-5">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8"  y1="2" x2="8"  y2="6" />
              <line x1="3"  y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <span className="text-white font-semibold text-lg tracking-tight">Alfasistemi</span>
        </div>

        {/* Testo centrale */}
        <div className="relative z-10 space-y-5">
          <h1 className="text-4xl font-bold text-white leading-snug">
            Pianificazione<br />delle Risorse
          </h1>
          <p className="text-blue-100 text-base leading-relaxed max-w-xs">
            Piattaforma interna per la gestione e la pianificazione mensile del personale.
          </p>

          <div className="pt-2 space-y-3">
            {[
              { icon: '▸', text: 'Pianificazione mensile per consulente' },
              { icon: '▸', text: 'Controllo accessi basato su ruolo' },
              { icon: '▸', text: 'Esportazione e reportistica Excel' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-start gap-3">
                <span className="text-blue-300 text-xs mt-0.5">{icon}</span>
                <span className="text-blue-100 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-blue-200/60 text-xs">
          © {new Date().getFullYear()} Alfa Sistemi S.p.A. — Uso interno
        </div>
      </div>

      {/* Pannello destro — login bianco */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <div className="w-full max-w-sm space-y-8">

          {/* Header mobile */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-4 h-4">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8"  y1="2" x2="8"  y2="6" />
                <line x1="3"  y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <span className="text-gray-800 font-semibold">Alfasistemi</span>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900">Accesso</h2>
            <p className="mt-2 text-sm text-gray-500">
              Esegui l'accesso con il tuo account aziendale per continuare.
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg viewBox="0 0 21 21" width="18" height="18" aria-hidden="true">
                <rect x="1"  y="1"  width="9" height="9" fill="#f25022" />
                <rect x="11" y="1"  width="9" height="9" fill="#7fba00" />
                <rect x="1"  y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Accedi con Microsoft
            </button>
          </div>

          <p className="text-xs text-gray-400 text-center">
            Accesso riservato al personale interno Alfasistemi.
          </p>

        </div>
      </div>

    </div>
  );
}
